<script>
import { mapState } from 'vuex';
import flatPickr from 'vue-flatpickr-component';
import { DateTime } from 'luxon';
import debounce from 'lodash/debounce';
import map from 'lodash/map';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Multiselect from 'vue-multiselect';
import BlockingContainer from '@components/BlockingContainer';
import Layout from '@layouts/main';
import ReportModel from '@models/Report';
import isFunction from 'lodash/isFunction';
import utility from '@mixins/utility';
import VipModel from '@models/Vip';
import CompanyModel from '@models/Company';
import EntityMultiselect from '@components/EntityMultiselect';
import HeaderComponent from '@components/HeaderComponent';

export default {
  page() {
    return {
      title: this.$t('titles.reports'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    BlockingContainer,
    flatPickr,
    Multiselect,
    EntityMultiselect,
    HeaderComponent,
  },
  mixins: [utility],
  props: {
    locations: {
      required: true,
      type: Array,
      default: null,
    },
    areas: {
      required: true,
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      vipModel: VipModel,
      companyModel: CompanyModel,
      sortBy: 'id',
      sortDesc: true,
      totalRows: 0,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        { key: 'selected', sortable: false, label: this.$t('table_labels.select') },
        { key: 'id', sortable: true, class: 'w-80' },
        { key: 'invoice_number', label: this.$t('table_labels.invoice_number'), sortable: false },
        {
          key: 'meeting_date',
          label: this.$t('table_labels.start_date'),
          sortable: true,
          formatter: (value) => (value == null ? '' : DateTime.fromSQL(value).toFormat('dd.MM.yyyy')),
        },
        { key: 'user_name', label: this.$t('table_labels.driver'), sortable: true },
        { key: 'vip_name', label: this.$t('table_labels.vip'), sortable: true },
        { key: 'meeting_point', label: this.$t('table_labels.city_and_location'), sortable: true },
        { key: 'total_service_time', label: this.$t('table_labels.total_service_time'), sortable: false, class: 'text-left' },
        { key: 'completed', label: this.$t('table_labels.status'), sortable: true, class: 'text-left' },
        { key: 'times_registered_custom', label: this.$t('fields.times_listed'), sortable: true, class: 'text-left' },
        { key: 'abroad', label: this.$t('fields.abroad'), sortable: true },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      isBusy: false,
      filters: {
        search: null,
        meetingDateStart: null,
        meetingDateEnd: null,
        areasAndLocations: null,
        drafts: 'none',
        lockStatus: 0,
        invoiceStatus: 0,
      },
      lockStatusFilterOptions: [
        { text: this.$t('search.lock_status.unlocked'), value: 0 },
        { text: this.$t('search.lock_status.locked'), value: 1 },
        { text: this.$t('search.lock_status.all'), value: 2 },
      ],
      invoiceStatusFilterOptions: [
        { text: this.$t('search.invoice.all_reports'), value: 0 },
        { text: this.$t('search.invoice.no_number'), value: 1 },
      ],
      adminDrafts: 0,
      allDrafts: 0,
      onlyDrafts: 0,
      focused: false,
      selectedItems: [],
      selectionOptionSelected: null,
      selectionOptions: [
        { text: this.$t('fields.select_option'), value: null },
        { text: this.$t('fields.export_select'), value: 0, disabled: this.selectAll },
        { text: this.$t('fields.invoice_select'), value: 1 },
        { text: this.$t('fields.multi_report_export_select'), value: 2 },
        { text: this.$t('fields.sum_service_times_select'), value: 5 },
        { text: this.$t('fields.change_customer'), value: 3 },
        { text: this.$t('fields.change_vip'), value: 4 },
      ],
      invoiceNumber: null,
      selectedVIP: null,
      selectedCustomer: null,
      selectedVIPId: null,
      selectedCustomerId: null,
      selectAll: false,
      reports: [],
      areasAndLocations: [],
      showMoreOptions: false,
      totalServiceTimeSum: null,
      serviceTimeDataMap: {},
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
    ...mapState({
      dateConfig: (state) => state.common.dateConfig,
    }),
  },
  watch: {
    filters: {
      handler(newFilterVal) {
        this.setDrafts(newFilterVal.drafts);
        this.$nextTick(() => {
          this.$refs.asyncTable.refresh();
        });
      },
      immediate: false,
      deep: true,
    },
    reports(newReports) {
      if (newReports.length > 0) {
        const pageItemMap = new Map(newReports.map((r) => [r.id, r]));
        // Re-enrich selectedItems for current-page items (restores correct expenses data)
        this.selectedItems = this.selectedItems.map((item) => {
          const pageItem = pageItemMap.get(item.id);
          if (pageItem) {
            return {
              user_id: pageItem.user.id,
              id: pageItem.id,
              completed: pageItem.completed,
              expenses: pageItem.expenses,
              export_expenses: pageItem.export_expenses,
            };
          }
          return item;
        });
        // Also update service time data for current-page items with fresh formatted values
        const updatedMap = { ...this.serviceTimeDataMap };
        newReports.forEach((r) => {
          updatedMap[r.id] = {
            total_service_time: r.total_service_time,
            only_costs: r.only_costs,
          };
        });
        this.serviceTimeDataMap = updatedMap;
      }
    },
  },
  mounted() {
    this.areasAndLocations = [
      {
        type: this.$t('fields.areas'),
        options: this.areas,
      },
      {
        type: this.$t('fields.locations'),
        options: this.locations,
      },
    ];
  },
  methods: {
    goToEditReport(reportId) {
      const routeData = this.$router.resolve({ name: 'reports.edit', params: { id: reportId } });
      window.open(routeData.href, '_blank');
    },
    setDrafts(state) {
      switch (state) {
        case 'none':
          this.allDrafts = 0;
          this.adminDrafts = 0;
          this.onlyDrafts = 0;
          break;
        case 'only_drafts':
          this.allDrafts = 0;
          this.adminDrafts = 0;
          this.onlyDrafts = 1;
          break;
        case 'all_drafts':
          this.allDrafts = 1;
          this.adminDrafts = 0;
          this.onlyDrafts = 0;
          break;
        case 'admin_drafts':
          this.allDrafts = 0;
          this.adminDrafts = 1;
          this.onlyDrafts = 0;
          break;
      }
      this.currentPage = 1;
    },
    checkIsSameUserClicked(selItem) {
      if (this.selectedItems.length > 0) {
        return this.selectedItems[0].user_id === selItem.user_id;
      }
      return true;
    },
    clearSelected() {
      this.selectedItems = [];
      this.selectAll = false;
      this.totalServiceTimeSum = null;
    },
    customLocationName({ name }) {
      return name;
    },
    itemsProvider(ctx) {
      const sortBy = ctx.sortBy === 'id' ? ctx.sortBy : ctx.sortBy + ',id';
      return ReportModel.orderBy(this.sortDir + sortBy)
        .include('company', 'vip', 'user', 'expenses', 'daily_allowances')
        .params({
          areas_and_locations: map(this.filters.areasAndLocations, 'id'),
          all_drafts: this.allDrafts,
          admin_drafts: this.adminDrafts,
          only_drafts: this.onlyDrafts,
          with_resource: true,
        })
        .where('search', this.filters.search)
        .where('meeting_date_start', this.filters.meetingDateStart)
        .where('meeting_date_end', this.filters.meetingDateEnd)
        .where('lock_status', this.filters.lockStatus)
        .where('invoice_status', this.filters.invoiceStatus)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.meta.total;
          this.reports = response.data;
          return items || [];
        })
        .catch(() => []);
    },
    async deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.report') }))) {
        try {
          this.$wait.start('report processing');
          const report = new ReportModel({ id });
          await report.delete();
          this.$refs.asyncTable.refresh();
        } catch (error) {
        } finally {
          this.$wait.end('report processing');
        }
      }
    },
    lock(id) {
      if (confirm(this.$t('alerts.lock_entity', { entity: this.$t('entities.report') }))) {
        this.$wait.start('report processing');
        new ReportModel({ id: `${id}/lock` })
          .save()
          .then(() => {
            this.$wait.end('report processing');
            this.$refs.asyncTable.refresh();
          })
          .catch(() => {
            this.$wait.end('report processing');
            this.$refs.asyncTable.refresh();
          });
      }
    },
    exportPdf(url, id) {
      this.$wait.start(`downloading${id}-${url}`);
      this.$http
        .get(`reports/${url}/${id}`, { responseType: 'blob' })
        .then((response) => {
          this.$wait.end(`downloading${id}-${url}`);
          const headers = response.headers;
          const blob = new Blob([response.data], { type: headers['content-type'] });
          const fileName = this.getFileNameFromContentDisposition(String(headers['content-disposition']));
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName !== '' ? fileName : `Report #${id} ${url === 'export_expenses_pdf' ? 'expenses' : ''}`;
          link.click();
        })
        .catch(() => this.$wait.end(`downloading${id}-${url}`));
    },
    exportMultiPdf(type) {
      this.$wait.start('global loading');
      const ids = map(this.selectedItems, 'id');

      let url = ``;

      switch (type) {
        case 'expenses':
          url = `admin/reports/export-all-expenses`;
          break;
        case 'reports':
          url = `admin/reports/export-multi-report`;
          break;
        default:
          url = `admin/reports/export-all-expenses`;
          break;
      }

      this.$http
        .get(url, { params: { ids: ids }, responseType: 'blob' })
        .then((response) => {
          this.$wait.end('global loading');
          this.clearSelected();
          const headers = response.headers;
          const blob = new Blob([response.data], { type: headers['content-type'] });
          const fileName = this.getFileNameFromContentDisposition(String(headers['content-disposition']));
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName !== '' ? fileName : `Selected reports exports`;
          link.click();
        })
        .catch(() => this.$wait.end('global loading'));
    },
    customAreaName({ name }) {
      return name;
    },
    updateFilter: debounce(function (value) {
      this.filters.search = value;
    }, 500),
    createInvoiceNumber(callback) {
      const reportIds = map(this.selectedItems, (item) => item.id);
      this.$validator.validateAll('invoice-number-form').then((result) => {
        if (result && reportIds.length > 0) {
          this.$wait.start('invoice-number-loading');
          this.$http
            .put('admin/reports/invoice-number', {
              report_ids: reportIds,
              invoice_number: [this.invoiceNumber],
            })
            .then(() => {
              if (callback && isFunction(callback)) callback();
              this.$wait.end('invoice-number-loading');
              this.clearSelected();
              this.invoiceNumber = null;
              this.$refs.asyncTable.refresh();
            })
            .catch(() => this.$wait.end('invoice-number-loading'));
        }
      });
    },
    updateAbroad(id, item) {
      this.$nextTick(() => {
        this.$wait.start(`toggleAbroad${id}`);
        new ReportModel({
          id: `${id}/update-abroad`,
          abroad: item.abroad,
        })
          .save()
          .then(() => {
            this.$wait.end(`toggleAbroad${id}`);
          });
      });
    },
    restore(id) {
      if (confirm(this.$t('alerts.restore_entity', { entity: this.$t('entities.report') }))) {
        this.$wait.start('report processing');
        new ReportModel({
          id: `${id}/restore`,
        })
          .save()
          .then(() => {
            this.$refs.asyncTable.refresh();
            this.$wait.end('report processing');
          })
          .catch(() => {
            this.$wait.end('report processing');
          });
      }
    },
    selectAllReports() {
      if (this.selectAll) {
        this.$wait.start('select-all-reports');
        const params = {
          areas_and_locations: map(this.filters.areasAndLocations, 'id'),
          all_drafts: this.allDrafts,
          admin_drafts: this.adminDrafts,
          only_drafts: this.onlyDrafts,
          'filter[lock_status]': this.filters.lockStatus,
          'filter[invoice_status]': this.filters.invoiceStatus,
        };
        if (this.filters.search) params['filter[search]'] = this.filters.search;
        if (this.filters.meetingDateStart) params['filter[meeting_date_start]'] = this.filters.meetingDateStart;
        if (this.filters.meetingDateEnd) params['filter[meeting_date_end]'] = this.filters.meetingDateEnd;

        const currentPageMap = new Map(this.reports.map((r) => [r.id, r]));

        this.$http
          .get('admin/reports/all-ids', { params })
          .then((response) => {
            const serviceTimeDataMap = {};
            this.selectedItems = response.data.data.map((item) => {
              serviceTimeDataMap[item.id] = {
                total_service_time: item.total_service_time,
                only_costs: item.only_costs,
              };
              const pageItem = currentPageMap.get(item.id);
              if (pageItem) {
                return {
                  user_id: pageItem.user.id,
                  id: pageItem.id,
                  completed: pageItem.completed,
                  expenses: pageItem.expenses,
                  export_expenses: pageItem.export_expenses,
                };
              }
              return {
                user_id: item.user_id,
                id: item.id,
                completed: item.completed,
                expenses: [],
                export_expenses: item.export_expenses,
              };
            });
            this.serviceTimeDataMap = serviceTimeDataMap;
          })
          .catch(() => {
            this.selectAll = false;
          })
          .finally(() => {
            this.$wait.end('select-all-reports');
          });
      } else {
        this.selectedItems = [];
      }
    },
    updateCustomer() {
      if (this.selectedCustomerId) {
        this.$wait.start(`report-update-customer`);
        const ids = map(this.selectedItems, 'id');
        this.$http
          .put(`admin/reports/update-customer`, { ids: ids, company_id: this.selectedCustomerId })
          .then(() => {
            this.$wait.end(`report-update-customer`);
            this.$refs.asyncTable.refresh();
            this.selectedCustomer = null;
            this.selectedCustomerId = null;
            this.clearSelected();
          })
          .catch(() => this.$wait.end(`report-update-customer`));
      }
    },
    updateVIP() {
      if (this.selectedVIPId) {
        this.$wait.start(`report-update-vip`);
        const ids = map(this.selectedItems, 'id');
        this.$http
          .put(`admin/reports/update-vip`, { ids: ids, vip_id: this.selectedVIPId })
          .then(() => {
            this.$wait.end(`report-update-vip`);
            this.$refs.asyncTable.refresh();
            this.selectedVIP = null;
            this.selectedVIPId = null;
            this.clearSelected();
          })
          .catch(() => this.$wait.end(`report-update-vip`));
      }
    },
    calculateTotalServiceTime() {
      let totalMinutes = 0;

      this.selectedItems.forEach((item) => {
        const data = this.serviceTimeDataMap[item.id];
        if (data && data.total_service_time && !data.only_costs) {
          const timeParts = data.total_service_time.split(':');
          const hours = parseInt(timeParts[0], 10) || 0;
          const minutes = parseInt(timeParts[1], 10) || 0;
          totalMinutes += (hours * 60) + minutes;
        }
      });

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      this.totalServiceTimeSum = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    },
  },
};
</script>
<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent
          :title="$t('titles.reports')"
          :button-text="$t('titles.create_report')"
          @button-pressed="$router.push({ name: 'reports.create' })"
        ></HeaderComponent>
      </b-col>
    </b-row>
    <b-row class="mb-2">
      <b-col sm="12" md="4" lg="4" class="mb-3">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.search') }}</span
            >&nbsp;
            <small class="pt-1">
              {{
                $t('search.message', {
                  entity: [$t('search.entities.vip'), $t('search.entities.driver'), $t('search.entities.license_plate'), $t('search.entities.car_type')].join(
                    ', '
                  ),
                })
              }}
            </small>
          </template>
        </HeaderComponent>
        <b-input-group>
          <b-form-input :value="filters.search" class="form-control-sm" :placeholder="$t('placeholders.search')" @input="updateFilter">&nbsp;</b-form-input>
          <b-input-group-append>
            <b-btn variant="secondary" size="sm" class="text-white border-gray" :disabled="!filters.search" @click="filters.search = ''">{{
              $t('actions.clear')
            }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col sm="12" md="4" lg="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('search.edit_status') }}</span>
          </template>
        </HeaderComponent>
        <b-form-group>
          <b-form-radio-group
            id="radio-group-drafts"
            v-model="filters.drafts"
            buttons
            size="sm"
            name="radio-sub-component"
            button-variant="outline-secondary border-gray"
            class="w-100"
          >
            <b-form-radio class="border-muted" value="none">{{ $t('fields.completed') }}</b-form-radio>
            <b-form-radio class="border-muted" value="only_drafts">{{ $t('fields.draft_only') }}</b-form-radio>
            <b-form-radio class="border-muted" value="all_drafts">{{ $t('fields.completed_and_draft') }}</b-form-radio>
          </b-form-radio-group>
        </b-form-group>
      </b-col>
      <b-col sm="12" md="4" lg="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('search.lock_status.label') }}</span>
          </template>
        </HeaderComponent>
        <b-form-group>
          <b-form-radio-group
            id="radio-group-lock-status"
            v-model="filters.lockStatus"
            :options="lockStatusFilterOptions"
            buttons
            size="sm"
            name="radio-sub-component"
            button-variant="outline-secondary border-gray"
            class="w-100"
          >
          </b-form-radio-group>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row class="mb-4 d-flex justify-content-end">
      <b-col>
        <b-btn
          class="fa-pull-right cursor-pointer border-gray"
          :variant="showMoreOptions ? 'secondary' : 'outline-secondary'"
          size="md"
          @click.stop="showMoreOptions = !showMoreOptions"
        >
        <FontAwesomeIcon :icon="['fas', 'sliders-h']" />
      </b-btn>
      </b-col>
    </b-row>
    <b-collapse v-model="showMoreOptions">
      <b-row>
        <b-col sm="12" md="4" lg="4">
          <HeaderComponent>
            <template v-slot:header>
              <span>{{ $t('fields.interval_start') }}</span>
            </template>
          </HeaderComponent>
          <b-form-group>
            <b-input-group style="padding-left: 1px">
              <flat-pickr v-model="filters.meetingDateStart" :config="dateConfig" class="form-control-sm" :placeholder="$t('placeholders.select_date')"
                >&nbsp;</flat-pickr
              >
              <span v-show="filters.meetingDateStart" class="clear-calendar" @click.stop="filters.meetingDateStart = ''">&times;</span>
              <b-input-group-append>
                <b-btn
                  variant="secondary"
                  size="sm"
                  class="text-white border-gray"
                  :disabled="!filters.meetingDateStart"
                  @click="filters.meetingDateStart = null"
                  >{{ $t('actions.clear') }}</b-btn
                >
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col sm="12" md="4" lg="4">
          <HeaderComponent>
            <template v-slot:header>
              <span>{{ $t('fields.interval_end') }}</span>
            </template>
          </HeaderComponent>
          <b-form-group>
            <b-input-group>
              <flat-pickr v-model="filters.meetingDateEnd" :config="dateConfig" class="form-control-sm" :placeholder="$t('placeholders.select_date')"
                >&nbsp;</flat-pickr
              >
              <span v-show="filters.meetingDateEnd" class="clear-calendar" @click.stop="filters.meetingDateEnd = ''">&times;</span>
              <b-input-group-append>
                <b-btn
                  variant="secondary"
                  size="sm"
                  class="text-white border-gray"
                  :disabled="!filters.meetingDateEnd"
                  @click="filters.meetingDateEnd = null"
                  >{{ $t('actions.clear') }}</b-btn
                >
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col sm="12" md="4" lg="4">
          <HeaderComponent>
            <template v-slot:header>
              <span>{{ $t('fields.areas_and_locations') }}</span>
            </template>
          </HeaderComponent>
          <b-form-group>
            <b-input-group>
              <div class="multiselect-container kks-multiselect-container-index form-control form-control-sm p-0" :class="{ focused: focused }">
                <Multiselect
                  v-model="filters.areasAndLocations"
                  :options="areasAndLocations"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :placeholder="$t('placeholders.areas_and_locations_multiple')"
                  :label="$t('fields.locations')"
                  :custom-label="customLocationName"
                  track-by="id"
                  group-values="options"
                  group-label="type"
                  @open="focused = true"
                  @close="focused = false"
                  >&nbsp;</Multiselect
                >
              </div>
              <b-input-group-append>
                <b-btn
                  variant="secondary"
                  size="sm"
                  class="text-white border-gray"
                  :disabled="!filters.areasAndLocations"
                  @click="filters.areasAndLocations = []"
                  >{{ $t('actions.clear') }}</b-btn
                >
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="mb-4">
        <b-col sm="12" md="4" lg="4" class="mb-3">
          <HeaderComponent>
            <template v-slot:header>
              <span>{{ $t('titles.selection_options') }}</span
              >&nbsp;
            </template>
          </HeaderComponent>
          <b-input-group size="sm">
            <b-form-select v-model="selectionOptionSelected" :options="selectionOptions" @change="clearSelected"></b-form-select>
            <b-input-group-append>
              <b-btn
                variant="secondary"
                size="sm"
                class="text-white border-gray"
                :disabled="selectionOptionSelected === null"
                @click="selectionOptionSelected = null;clearSelected()"
                >{{ $t('actions.clear') }}</b-btn
              >
            </b-input-group-append>
          </b-input-group>
        </b-col>
        <b-col v-if="selectionOptionSelected === 4" sm="12" md="4" lg="4" class="mb-3">
          <HeaderComponent>
            <template v-slot:header>
              <span>{{ $t('search.entities.vip') }}</span>
            </template>
          </HeaderComponent>
          <b-input-group>
            <div class="multiselect-container kks-multiselect-container-index form-control form-control-sm p-0">
              <EntityMultiselect
                v-model="selectedVIPId"
                data-vv-name="vip_id"
                :data-vv-as="$t('fields.vip')"
                :initial-value="selectedVIP"
                :placeholder="$t('placeholders.vip')"
                :model="vipModel"
              >
              </EntityMultiselect>
            </div>
            <b-input-group-append>
              <b-button
                block
                type="submit"
                size="sm"
                class="text-white"
                :disabled="selectedItems.length === 0 || !selectedVIPId"
                variant="secondary"
                @click.stop="updateVIP"
              >
                {{ $t('actions.update') }}
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-col>
        <b-col v-else-if="selectionOptionSelected === 3" sm="12" md="4" lg="4" class="mb-3">
          <HeaderComponent>
            <template v-slot:header>
              <span>{{ $t('search.entities.customer') }}</span>
            </template>
          </HeaderComponent>
          <b-input-group>
            <div class="multiselect-container kks-multiselect-container-index form-control form-control-sm p-0">
              <EntityMultiselect
                v-model="selectedCustomerId"
                v-validate="''"
                data-vv-name="company_id"
                :data-vv-as="$t('fields.customer')"
                :initial-value="selectedCustomer"
                :placeholder="$t('placeholders.customer')"
                :model="companyModel"
              >
              </EntityMultiselect>
            </div>
            <b-input-group-append>
              <b-button
                block
                size="sm"
                type="submit"
                :disabled="selectedItems.length === 0 || !selectedCustomerId"
                variant="secondary"
                class="text-white"
                @click.stop="updateCustomer"
              >
                {{ $t('actions.update') }}
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-col>
        <b-col v-else-if="selectionOptionSelected === 1" sm="12" md="4" lg="4" class="mb-3">
          <HeaderComponent>
            <template v-slot:header>
              <span>{{ $t('fields.invoice_number') }}</span>
            </template>
          </HeaderComponent>
          <b-form data-vv-scope="invoice-number-form" @submit.prevent="createInvoiceNumber">
            <b-input-group>
              <b-form-input
                v-model="invoiceNumber"
                v-validate="'required'"
                size="sm"
                data-vv-value-path="localValue"
                data-vv-name="invoiceNumber"
                :data-vv-as="$t('fields.invoice_number')"
                type="text"
                :state="errors.has('invoice_number', 'invoice-number-form') ? false : null"
                aria-describedby="input-feedback-invoice_number"
                :placeholder="$t('placeholders.invoice_number')"
                >&nbsp;</b-form-input
              >
              <b-form-invalid-feedback id="input-feedback-invoice_number">
                <template v-show="errors.has('invoice_number', 'invoice-number-form')">{{ errors.first('invoice_number', 'invoice-number-form') }}</template>
              </b-form-invalid-feedback>
              <b-input-group-append>
                <b-button
                  block
                  type="submit"
                  size="sm"
                  :disabled="errors.any('invoice-number-form') || selectedItems.length === 0"
                  :variant="!errors.any('invoice-number-form') ? 'secondary' : 'outline-danger'"
                >
                  {{ $t('actions.update') }}
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form>
        </b-col>
        <b-col v-else-if="selectionOptionSelected === 2 || selectionOptionSelected === 0" sm="12" md="4" lg="4" class="mb-3">
          <b-button
            block
            :disabled="selectedItems.length === 0"
            class="mb-2"
            size="sm"
            variant="secondary"
            @click="exportMultiPdf(selectionOptionSelected === 0 ? 'expenses' : 'reports')"
            >{{ selectionOptionSelected === 0 ? $t('titles.export_multi_expenses_report') : $t('titles.export_multi_reports') }}</b-button
          >
        </b-col>
        <b-col v-else-if="selectionOptionSelected === 5" sm="12" md="4" lg="4" class="mb-3">
          <HeaderComponent>
            <template v-slot:header>
              <span>{{ $t('table_labels.total_service_time') }}</span>
            </template>
          </HeaderComponent>
          <b-input-group>
            <b-form-input
              v-model="totalServiceTimeSum"
              size="sm"
              type="text"
              readonly
              :placeholder="$t('placeholders.total_service_time')"
            ></b-form-input>
            <b-input-group-append>
              <b-button
                block
                size="sm"
                :disabled="selectedItems.length === 0"
                variant="secondary"
                class="text-white"
                @click="calculateTotalServiceTime"
              >
                {{ $t('actions.sum') }}
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-col>
        <b-col sm="12" md="4" lg="4">
          <HeaderComponent>
            <template v-slot:header>
              <span>{{ $t('fields.invoice_number') }}</span>
            </template>
          </HeaderComponent>
          <b-form-group>
            <b-form-radio-group
              id="radio-group-invoice-status"
              v-model="filters.invoiceStatus"
              :options="invoiceStatusFilterOptions"
              buttons
              size="sm"
              name="radio-sub-component"
              button-variant="outline-secondary border-gray"
              class="w-100"
            >
            </b-form-radio-group>
          </b-form-group>
        </b-col>
      </b-row>
    </b-collapse>
    <div id="reports-index" class="position-relative">
      <BlockingContainer v-if="$wait.is('report processing') || isBusy">&nbsp;</BlockingContainer>
      <b-table
        ref="asyncTable"
        small
        striped
        :items="itemsProvider"
        stacked="md"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
      >
        <template #head(selected)="data">
          <span>{{ data.label }}</span>
          <span>
            <FontAwesomeIcon v-if="$wait.is('select-all-reports')" icon="spinner" spin />
            <b-form-checkbox v-else id="checkboxSelectAll" v-model="selectAll" @change="selectAllReports"> </b-form-checkbox>
          </span>
        </template>
        <!-- custom sorting icons -->
        <template #head()="{ label, field: { key, sortable } }">
          {{ label }}
          <template v-if="sortable">
            <!-- Custom icons -->
            <template>
              <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
              <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
              <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
            </template>
          </template>
        </template>

        <template v-slot:cell(selected)="data">
          <b-form-checkbox
            v-if="data.item.user != null"
            :id="'checkbox' + data.index"
            v-model="selectedItems"
            :value="{
              user_id: data.item.user.id,
              id: data.item.id,
              completed: data.item.completed,
              expenses: data.item.expenses,
              export_expenses: data.item.export_expenses,
            }"
          >
          </b-form-checkbox>
        </template>
        <template v-slot:cell(invoice_number)="row">
          <span v-if="row.item.invoice_number" class="text-success">{{ row.item.invoice_number.join(',') }}</span>
          <span v-else class="text-success"> - </span>
        </template>
        <template v-slot:cell(completed)="row">
          <b-badge :variant="`${row.item.completed ? 'success' : 'danger'}`">{{ row.item.completed ? 'Completed' : 'Draft' }}</b-badge>
        </template>
        <template v-slot:cell(total_service_time)="row">
          <span v-if="!row.item.only_costs"> {{ row.item.total_service_time }} </span>
          <span v-else> - </span>
        </template>
        <template v-slot:cell(times_registered_custom)="row">
          <FontAwesomeIcon
            v-if="!row.item.only_costs"
            :icon="['fas', row.item.times_registered ? 'check-circle' : 'times-circle']"
            :class="row.item.times_registered ? 'text-success' : 'text-danger'"
          />
          <span v-else>
            <b-badge variant="primary" class="rounded-circle">C</b-badge>
          </span>
        </template>       
        <template v-slot:cell(actions)="row">
          <b-dropdown variant="outline-primary" dropleft no-caret size="sm">
            <template #button-content>
              <FontAwesomeIcon :icon="['fas', 'ellipsis-h']" />
            </template>

            <template v-if="row.item.deleted_at === null">
              <b-dropdown-item variant="primary" @click="goToEditReport(row.item.id)">
                <!-- only super admins and users with accounting role can edit locked report -->
                <FontAwesomeIcon v-if="isReportEditable(row.item.locked)" :icon="['fas', 'edit']" />
                <FontAwesomeIcon v-else :icon="['fas', 'eye']" />
                <small>&nbsp;{{ $t('actions.edit') }}</small>
              </b-dropdown-item>

              <b-dropdown-item variant="primary" :disabled="$wait.is(`downloading${row.item.id}-export_pdf`)" @click="exportPdf('export_pdf', row.item.id)">
                <FontAwesomeIcon :icon="['fas', 'file-export']" />
                <FontAwesomeIcon v-if="$wait.is(`downloading${row.item.id}-export_pdf`)" class="ml-1" icon="spinner" spin />
                <small>&nbsp;{{ $t('actions.export_report') }}</small>
              </b-dropdown-item>

              <b-dropdown-item
                variant="success"
                :disabled="$wait.is(`downloading${row.item.id}-export_expenses_pdf`)"
                @click="exportPdf('export_expenses_pdf', row.item.id)"
              >
                €
                <FontAwesomeIcon v-if="$wait.is(`downloading${row.item.id}-export_expenses_pdf`)" class="ml-1" icon="spinner" spin />
                <small>&nbsp;{{ $t('actions.export_expenses') }}</small>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="row.item.daily_allowances.length > 0"
                variant="success"
                :disabled="$wait.is(`downloading${row.item.id}-export_allowances_pdf`)"
                @click="exportPdf('export-daily-allowances', row.item.id)"
              >
                <FontAwesomeIcon :icon="['fa', 'utensils']" />
                <FontAwesomeIcon v-if="$wait.is(`downloading${row.item.id}-export_allowances_pdf`)" class="ml-1" icon="spinner" spin />
                <small>&nbsp;{{ $t('actions.export_allowances') }}</small>
              </b-dropdown-item>


              <b-dropdown-item
                variant="warning"
                :class="row.item.locked ? `lockedIcon` : ``"
                :disabled="row.item.locked === 1 && $can.not('doAccounting')"
                @click.stop="lock(row.item.id)"
              >
                <FontAwesomeIcon v-if="!row.item.locked" :icon="['fas', 'lock']" />
                <FontAwesomeIcon v-else :icon="['fas', 'lock-open']" />
                <small v-if="row.item.locked">&nbsp;{{ $t('actions.unlock') }}</small>
                <small v-else>&nbsp;&nbsp;{{ $t('actions.lock') }}</small>
              </b-dropdown-item>

              <b-dropdown-item v-if="row.item.deleted_at === null" variant="danger" @click.stop="deleteEntity(row.item.id)">
                <FontAwesomeIcon :icon="['fas', 'times-circle']" />
                <small>&nbsp;{{ $t('actions.delete') }}</small>
              </b-dropdown-item>
            </template>
            <b-dropdown-item v-if="row.item.deleted_at != null" v-can:superAdministrate variant="success" @click.stop="restore(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'trash-restore']" />
              <small>&nbsp;{{ $t('actions.restore') }}</small>
            </b-dropdown-item>
          </b-dropdown>
        </template>
        <template v-slot:cell(user_name)="row">
          <span v-if="row.item.user">{{ row.item.user.name }}</span>
        </template>
        <template v-slot:cell(vip_name)="row">
          {{ row.item.vip ? row.item.vip.name : row.item.vip_description }}
        </template>
        <template v-slot:cell(id)="row">
          <FontAwesomeIcon v-if="row.item.locked" :icon="['fas', 'lock']" />
          <FontAwesomeIcon v-else :icon="['fas', 'lock-open']" />
          {{ row.item.id }}
        </template>
        <template v-slot:cell(abroad)="row">
          <div v-if="!row.item.only_costs" :class="$style.statusContainer">
            <div v-show="!$wait.is(`toggleAbroad${row.item.id}`)">
              <b-form-checkbox v-model="row.item.abroad" :value="1" :unchecked-value="0" @change="updateAbroad(row.item.id, row.item)">&nbsp;</b-form-checkbox>
            </div>
            <FontAwesomeIcon v-show="$wait.is(`toggleAbroad${row.item.id}`)" :class="$style.statuLoadingIcon" :icon="['fas', 'spinner']" pulse />
          </div>
          <span v-else> - </span>
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
      </b-table>
    </div>
    <b-row>
      <b-col sm="12">
        <div class="d-flex justify-content-start align-items-left mt-2">
          <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
          <div class="text-secondary d-flex align-items-center justify-content-center mb-3 px-4">
            {{  $t('infos.showing_results', {
              start: (currentPage - 1) * perPage + 1,
              end: Math.min(currentPage * perPage, totalRows),
              total: totalRows
            }) }}
          </div>
        </div>
      </b-col>
    </b-row>
</Layout>
</template>
<style module>
.lockedIcon {
  color: #212529;
  background-color: #ffc107;
  border-color: #ffc107;
}

.lockedIcon:hover {
  border-color: #212529;
}

.statusContainer {
  margin: 0;
}

.statusLoadingIcon {
  margin: 0 auto;
}
</style>
